import React, { useEffect, useState } from 'react';
import { GitBranch, LockKeyhole } from 'lucide-react';
import api from '../../services/managedAxios';
import { LinkReconciliation } from './LinkReconciliation';
import { SourceCorrections } from './SourceCorrections';

const text = value => value == null ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);
const label = key => key.replaceAll('_', ' ');
const Values = ({ values, prefix }) => <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
  {Object.entries(values).filter(([key]) => key !== 'id').map(([key, value]) => <div key={key} className="min-w-0" data-testid={`${prefix}-${key}`}>
    <dt className="text-xs capitalize text-slate-500">{label(key)}</dt><dd className="break-words whitespace-pre-wrap">{text(value)}</dd>
  </div>)}
</dl>;

export const RecordEffects = ({ module, recordId }) => {
  const [data, setData] = useState(null), [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setData(null); setError('');
    api.get('/api/global-audit/effects', { params: { module, record_id: recordId } }).then(r => active && setData(r.data))
      .catch(e => active && setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Linked records could not be loaded'));
    return () => { active = false; };
  }, [module, recordId]);
  if (error) return <p role="alert" className="text-sm text-red-700" data-testid="record-effects-error">{error}</p>;
  if (!data) return <p data-testid="record-effects-loading">Loading linked records…</p>;
  return <div className="space-y-5" data-testid="record-effects-panel">
    <section className="border-b pb-4 space-y-3">
      <h2 className="text-lg font-semibold flex gap-2 items-center" data-testid="record-effects-source"><GitBranch size={18} />{data.source.label}</h2>
      {data.source.deleted && <p className="text-sm text-amber-800" data-testid="record-source-deleted">Source deleted · retained audit record</p>}
      <Values values={data.source.recorded_values || {}} prefix="record-source-value" />
    </section>
    <LinkReconciliation reconciliation={data.reconciliation} />
    <SourceCorrections module={module} recordId={recordId} actions={data.actions} />
    <p className="text-xs text-slate-500" data-testid="record-effects-basis">{data.basis}</p>
    {data.groups.map(group => <section className="border-b pb-4 space-y-3" key={group.key} data-testid={`effects-group-${group.key}`}>
      <h2 className="text-base font-semibold">{group.title}</h2>
      {group.restricted ? <p className="text-sm text-slate-500 flex gap-2" data-testid={`effects-restricted-${group.key}`}><LockKeyhole size={15} />Separate access required</p> : <>
        {group.rows.map(row => <details key={row.id} className="text-sm" data-testid={`effect-row-${group.key}-${row.id}`}>
          <summary className="cursor-pointer break-all text-teal-800" data-testid={`effect-expand-${group.key}-${row.id}`}>{row.payment_no || row.bill_no || row.date || row.txn_date || row.receipt_date || row.id}</summary>
          <div className="border-l-2 border-teal-100 pl-3 mt-3"><Values values={row} prefix={`effect-value-${group.key}-${row.id}`} /></div>
        </details>)}
        {!group.rows.length && <p className="text-xs text-slate-500" data-testid={`effects-empty-${group.key}`}>No linked records found.</p>}
        {group.has_more && <p className="text-xs text-amber-800" data-testid={`effects-limited-${group.key}`}>First 50 linked records shown; open the source register for the full history.</p>}
      </>}
    </section>)}
    {data.coverage === 'history_only' && <p className="text-sm text-slate-500" data-testid="record-effects-history-only">Linked-effect mapping is not available for this record type. Its recorded changes remain available in History.</p>}
    <p className="text-xs text-slate-500" data-testid="record-effects-empty-meaning">{data.empty_meaning}</p>
  </div>;
};